import { FileUpload } from '../Api/FileUpload';
import { FileDelete } from '../Api/FileDelete';
import { FileDownload } from '../Api/FileDownload';
import { validateFile } from './fileValidation';
import { toast } from 'react-toastify';

/**
 * Standard file upload handler
 * @param {File} file - The file to upload
 * @param {Object} options - Upload options
 * @param {string} options.relatedModelType - The type of model this file relates to (e.g., 'country', 'organization')
 * @param {string} options.createdBy - The ID of the user creating the file
 * @param {Object} options.validationConfig - File validation configuration
 * @param {Function} options.onSuccess - Callback function on successful upload
 * @param {Function} options.onError - Callback function on upload error
 */
export const handleFileUpload = async (file, options) => {
  const {
    relatedModelType,
    createdBy,
    validationConfig,
    onSuccess,
    onError
  } = options;

  try {
    // Validate file
    if (validationConfig) {
      const isValid = validateFile(file, validationConfig);
      if (!isValid) {
        throw new Error('Invalid file type or size');
      }
    }

    // Prepare upload data
    const uploadData = {
      file,
      file_name: file.name,
      file_type: file.type,
      mime_type: file.type,
      created_by: createdBy,
      file_size: file.size,
      related_model_type: relatedModelType,
    };

    // Upload file
    const response = await FileUpload(uploadData);
    
    if (response?.data?.data) {
      const fileData = {
        id: response.data.data.id,
        uploadId: response.data.data.upload_id,
        url: response.data.data.sas_url,
        fileName: file.name,
        fileSize: file.size,
      };

      onSuccess?.(fileData);
      return fileData;
    }
    
    throw new Error('File upload failed');
  } catch (error) {
    onError?.(error);
    toast.error(error.message || 'Failed to upload file');
    return null;
  }
};

/**
 * Standard file delete handler
 * @param {Object} fileData - The file data to delete
 * @param {string} fileData.id - The file ID
 * @param {string} fileData.uploadId - The upload ID
 * @param {Function} onSuccess - Callback function on successful deletion
 * @param {Function} onError - Callback function on deletion error
 */
export const handleFileDelete = async (fileData, onSuccess, onError) => {
  try {
    const response = await FileDelete(fileData.id, fileData.uploadId);
    if (response?.status === 200) {
      onSuccess?.();
      toast.success('File deleted successfully');
      return true;
    }
    throw new Error('Failed to delete file');
  } catch (error) {
    onError?.(error);
    toast.error(error.message || 'Failed to delete file');
    return false;
  }
};

/**
 * Standard file download handler
 * @param {string} url - The file URL to download
 * @param {string} fileName - The name to save the file as
 * @param {Function} onError - Callback function on download error
 */
export const handleFileDownload = async (url, fileName, onError) => {
  try {
    await FileDownload(url, fileName);
  } catch (error) {
    onError?.(error);
    toast.error('Failed to download file');
  }
};

/**
 * Standard file change handler for input elements
 * @param {Event} event - The file input change event
 * @param {Object} options - Handler options
 * @param {Object} options.validationConfig - File validation configuration
 * @param {Function} options.onSuccess - Callback function on successful file selection
 * @param {Function} options.onError - Callback function on file selection error
 */
export const handleFileChange = async (event, options) => {
  const { validationConfig, onSuccess, onError } = options;
  const file = event.target.files[0];

  if (!file) return;

  try {
    if (validationConfig) {
      const isValid = validateFile(file, validationConfig);
      if (!isValid) {
        throw new Error('Invalid file type or size');
      }
    }

    const fileData = {
      file,
      fileName: file.name,
      fileSize: file.size,
      fileUrl: URL.createObjectURL(file)
    };

    onSuccess?.(fileData);
    return fileData;
  } catch (error) {
    onError?.(error);
    toast.error(error.message || 'Failed to process file');
    return null;
  } finally {
    // Reset file input
    event.target.value = null;
  }
}; 